/**
 * Reportes PDF de resultados de actividades para premiacion.
 *
 * Genera una pagina (carta) por actividad con:
 * - Posicion (dense ranking: empates comparten puesto)
 * - Club
 * - Resultado capturado (segun modo de puntaje)
 * - Puntos
 */
import { jsPDF } from "jspdf";
import type {
  ActivityScoreConfig,
  ActivityScoreInput,
  ActivityScoreMode,
  ClubActivityScore,
  EventActivity,
} from "@/types";

interface ReportActivity {
  id: string;
  name: string;
  scoreMode: EventActivity["scoreMode"];
  scoreConfig: EventActivity["scoreConfig"];
}

export interface ActivityResultsSection {
  categoryName: string;
  activity: ReportActivity;
  scores: ClubActivityScore[];
}

interface DownloadResultsBookParams {
  eventName: string;
  sections: ActivityResultsSection[];
  filenameBase?: string;
}

interface RankingRow {
  clubName: string;
  points: number;
  resultLabel: string;
  position: number;
}

const PAGE_MARGIN = 14; // mm
const MODE_LABEL: Record<ActivityScoreMode, string> = {
  podium: "Podio (1/2/3/Participacion)",
  pass_fail: "Cumple / No cumple",
  fixed: "Entrego / No entrego",
  timing: "Por fecha de entrega",
  deductive: "Deductivo",
  accumulative: "Acumulativo",
};

function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatResultLabel(
  mode: ActivityScoreMode,
  config: ActivityScoreConfig,
  input: ActivityScoreInput | undefined,
): string {
  if (!input) return "Sin registro";
  switch (mode) {
    case "podium": {
      if (input.position === "first") return "1 lugar";
      if (input.position === "second") return "2 lugar";
      if (input.position === "third") return "3 lugar";
      if (input.position === "participation") return "Participacion";
      return "Sin registro";
    }
    case "pass_fail":
      if (input.passed === true) return "Cumple";
      if (input.passed === false) return "No cumple";
      return "Sin registro";
    case "fixed":
      if (input.delivered === true) return "Entrego";
      if (input.delivered === false) return "No entrego";
      return "Sin registro";
    case "timing": {
      const idx = input.timingBandIndex;
      if (idx === undefined || idx === null) return "Sin registro";
      const bands = config.timingBands ?? [];
      return bands[idx]?.label || `Banda ${idx + 1}`;
    }
    case "deductive": {
      if (input.deductionPoints === undefined) return "Sin registro";
      return `Faltas: ${input.deductionPoints}`;
    }
    case "accumulative": {
      if (
        input.unitsCompleted === undefined &&
        input.penaltyUnits === undefined
      ) {
        return "Sin registro";
      }
      const units = input.unitsCompleted ?? 0;
      const pen = input.penaltyUnits ?? 0;
      return `Unid: ${units} / Penal: ${pen}`;
    }
    default:
      return "Sin registro";
  }
}

function buildRankingRows(section: ActivityResultsSection): RankingRow[] {
  const rows = section.scores
    .map((s) => ({
      clubName: s.clubName,
      points: s.points ?? 0,
      resultLabel: formatResultLabel(
        section.activity.scoreMode,
        section.activity.scoreConfig,
        s.input,
      ),
      position: 0,
    }))
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      return a.clubName.localeCompare(b.clubName, "es");
    });

  let currentPos = 0;
  let lastPoints: number | null = null;
  rows.forEach((r) => {
    if (lastPoints === null || r.points !== lastPoints) {
      currentPos += 1;
      lastPoints = r.points;
    }
    r.position = currentPos;
  });
  return rows;
}

function drawHeader(
  pdf: jsPDF,
  section: ActivityResultsSection,
  generatedAtLabel: string,
  eventName: string,
): number {
  const pageW = pdf.internal.pageSize.getWidth();
  const usableW = pageW - PAGE_MARGIN * 2;
  let y = PAGE_MARGIN;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.text("RESULTADOS OFICIALES - ACTIVIDAD", PAGE_MARGIN, y + 2);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.text(generatedAtLabel, pageW - PAGE_MARGIN, y + 2, { align: "right" });
  y += 9;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(section.activity.name, PAGE_MARGIN, y);
  y += 5;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(80);
  const line = `${eventName}  ·  ${section.categoryName}  ·  ${MODE_LABEL[section.activity.scoreMode]}`;
  const lineParts = pdf.splitTextToSize(line, usableW);
  pdf.text(lineParts, PAGE_MARGIN, y);
  y += lineParts.length * 4;
  pdf.setTextColor(0);
  return y + 3;
}

function drawCardsPage(
  pdf: jsPDF,
  rows: RankingRow[],
  startY: number,
): number {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - PAGE_MARGIN * 2;
  const gap = 3;
  const cols = 2;
  const minCardH = 8;
  const bottomLimit = pageH - PAGE_MARGIN;
  const availableH = Math.max(30, bottomLimit - startY);
  const gridRowsCapacity = Math.max(
    1,
    Math.floor((availableH + gap) / (minCardH + gap)),
  );
  const maxCards = gridRowsCapacity * cols;
  const chunk = rows.slice(0, maxCards);
  if (chunk.length === 0) return 0;

  const gridRows = Math.ceil(chunk.length / cols);
  const cardW = (usableW - gap) / 2;
  const cardH = Math.min(
    18,
    Math.max(8, (availableH - gap * (gridRows - 1)) / gridRows),
  );
  const compact = cardH < 10;

  chunk.forEach((r, idx) => {
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    const x = PAGE_MARGIN + col * (cardW + gap);
    const y = startY + row * (cardH + gap);

    if (r.position === 1) pdf.setFillColor(255, 248, 225);
    else if (r.position === 2) pdf.setFillColor(243, 244, 246);
    else if (r.position === 3) pdf.setFillColor(255, 237, 213);
    else pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(210);
    pdf.roundedRect(x, y, cardW, cardH, 1.8, 1.8, "FD");

    const posW = 17;
    const pointsW = 30;
    const textX = x + posW + 1.5;
    const textW = Math.max(24, cardW - posW - pointsW - 3);
    const centerY = y + cardH * 0.62;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(compact ? 8 : 10);
    pdf.text(`#${r.position}`, x + 2, centerY);

    pdf.setFont("helvetica", r.position <= 3 ? "bold" : "normal");
    pdf.setFontSize(compact ? 7.5 : 8.8);
    const club = pdf.splitTextToSize(r.clubName, textW)[0] as string;
    pdf.text(club, textX, y + cardH * 0.42);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(compact ? 6.3 : 7.1);
    const result = pdf.splitTextToSize(r.resultLabel, textW)[0] as string;
    pdf.setTextColor(95);
    pdf.text(result, textX, y + cardH * 0.78);
    pdf.setTextColor(0);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(compact ? 7.6 : 8.8);
    pdf.text(
      `${r.points.toLocaleString("es-CO")} pts`,
      x + cardW - 1.6,
      centerY,
      { align: "right" },
    );
  });

  return chunk.length;
}

function drawRows(pdf: jsPDF, rows: RankingRow[], startY: number): void {
  let remaining = rows;
  let isFirstChunk = true;
  while (remaining.length > 0) {
    if (!isFirstChunk) {
      pdf.addPage();
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(9);
      pdf.setTextColor(90);
      pdf.text("Continuacion de resultados", PAGE_MARGIN, PAGE_MARGIN);
      pdf.setTextColor(0);
    }
    const y = isFirstChunk ? startY : PAGE_MARGIN + 4;
    const consumed = drawCardsPage(pdf, remaining, y);
    if (consumed <= 0) break;
    remaining = remaining.slice(consumed);
    isFirstChunk = false;
  }
}

function drawActivityResultsPage(
  pdf: jsPDF,
  section: ActivityResultsSection,
  isFirst: boolean,
  eventName: string,
): void {
  if (!isFirst) pdf.addPage();
  const generatedAt = new Date().toLocaleString("es-CO");
  const y = drawHeader(pdf, section, generatedAt, eventName);
  const rows = buildRankingRows(section);

  if (rows.length === 0) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(10);
    pdf.setTextColor(110);
    pdf.text(
      "Sin puntajes registrados para esta actividad.",
      PAGE_MARGIN,
      y + 9,
    );
    pdf.setTextColor(0);
    return;
  }
  drawRows(pdf, rows, y);
}

export function downloadActivityResultsBookPdf(
  params: DownloadResultsBookParams,
): void {
  if (!params.sections.length) return;
  const pdf = new jsPDF({ unit: "mm", format: "letter" });
  params.sections.forEach((section, idx) => {
    drawActivityResultsPage(pdf, section, idx === 0, params.eventName);
  });
  const base =
    params.filenameBase || `resultados-actividades-${slug(params.eventName)}`;
  pdf.save(`${base}.pdf`);
}
